import React from "react";

import styled from "@emotion/styled";

import Text, { TTextSize } from "@components/Text";
import HoverTooltip from "@components/Tooltip";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import { ReactComponent as ArrowDownIcon } from "@assets/icons/arrowDown.svg";
import { ReactComponent as InfoIcon } from "@assets/icons/info.svg";

// When `inline`, the dropdown flows in normal layout (e.g. inside a flex
// control bar) instead of being absolutely pinned to the map corner.
const Wrap = styled.div<{ inline?: boolean }>`
  position: ${({ inline }) => (inline ? "relative" : "static")};
`;

const Root = styled.div<{ opened?: boolean; inline?: boolean; minWidth?: string }>`
  display: flex;
  align-items: center;
  position: ${({ inline }) => (inline ? "relative" : "absolute")};
  ${({ inline }) => (inline ? "" : "inset: 18px auto auto 24px;")}
  padding: 6px 8px 6px 12px;
  box-sizing: border-box;
  background: #ffffff;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  border-radius: 4px;
  /* Above the TracksScreen device legend (z 20) and the export menu (z 30),
     which share this stacking context and previously painted over the open
     option list. The inline variant keeps its own lower value below. */
  z-index: ${({ inline }) => (inline ? 1 : 40)};
  cursor: pointer;
  transition: 0.4s;
  min-width: ${({ minWidth }) => minWidth ?? "200px"};
  & > .arrow {
    min-width: 20px;
    height: auto;
    transition: 0.4s;
    transform: rotate(${({ opened }) => (opened ? -90 : 0)}deg);
    path {
      fill: #494c5b;
    }
  }
`;

const Tooltip = styled.div<{ opened: boolean; inline?: boolean; minWidth?: string }>`
  width: fit-content;
  position: absolute;
  ${({ inline }) => (inline ? "left: 0; top: calc(100% + 6px);" : "left: 24px; top: 54px;")}
  background: #ffffff;
  height: ${({ opened }) => (opened ? "auto" : 0)};
  border: ${({ opened }) => (opened ? 1 : 0)}px solid #cbd5d8;
  padding: ${({ opened }) => (opened ? "8px 0" : "0")};
  box-sizing: border-box;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
  border-radius: 6px;
  max-height: 300px;
  overflow: ${({ opened }) => (opened ? "auto" : "hidden")};
  transition: 0.4s;
  /* Must stay at or above Root so the open list clears the device legend. */
  z-index: ${({ inline }) => (inline ? 5 : 40)};
  min-width: ${({ minWidth }) => minWidth ?? "200px"};
`;

const SelectRow = styled(Text) <{ selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  box-sizing: border-box;
  cursor: pointer;
  background: ${({ selected }) => (selected ? "#f8f8f8" : "#fff")};
  transition: 0.4s;
  width: 100%;
  :hover {
    background: #f8f8f8;
  }
`;

// Small info affordance shown after an option's title; hovering it reveals the
// option's `info` text (localized) without selecting the option.
const InfoTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  svg {
    width: 14px;
    height: 14px;
  }
`;


// Marks an option as user-supplied rather than one of the built-in layers.
const Badge = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #68ad18;
  background: rgba(104, 173, 24, 0.12);
  border-radius: 3px;
  padding: 1px 5px;
  white-space: nowrap;
`;

// Option is a configuration for a single dropdown option.
export type Option = {
  // Human-readable title of the option
  title: string;
  // Machine value of the option
  value: string;
  // Optional explanation shown in a hover tooltip via a small (i) icon.
  info?: string;
  // Short label shown next to the title, e.g. "Imported".
  badge?: string;
  // An option that performs an action instead of selecting a value (e.g. "add
  // a layer"). It fires onSelect but never becomes the dropdown's shown value,
  // which would otherwise leave the header naming a view that isn't displayed.
  action?: boolean;
};

export type DropdownOptions = {
  // Dropdown title/hint
  title?: string;
  // Dropdown options from which user can select.
  // NOTE(nk2ge5k): first value is used as default value
  options: Option[];
  // selected option value
  selected?: string,
  // Callback that will be triggered when user selects an option.
  onSelect: (value: string) => void,
  // When true, the dropdown flows inline (relative) instead of being
  // absolutely pinned to the map corner.
  inline?: boolean,
  // Overrides the default 200px min-width (e.g. "auto" for compact pills).
  minWidth?: string,
}

const Dropdown: React.FC<DropdownOptions> = ({ title, options, selected, onSelect, inline, minWidth }) => {
  let selectedOption = options.find((option) => option.value === selected) ?? options[0];

  const [opened, setOpened] = React.useState(false);
  const [current, setSelected] = React.useState(selectedOption);

  // The open list is opaque and now paints above the map legends, so it needs a
  // way out other than re-clicking the header.
  const wrapRef = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(wrapRef, () => setOpened(false));

  // Follow `selected` when the parent drives it (e.g. TracksScreen forcing the
  // layer back to Sensor when camera data disappears). Re-selecting the option
  // the user just clicked is a no-op, so this doesn't fight local clicks.
  React.useEffect(() => {
    const next = options.find((option) => option.value === selected);
    if (next) setSelected(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  if (options.length === 0) {
    return null;
  }

  // NOTE(nk2ge5k): do not blame me it is a copypasted code which i do not
  // understand completely
  const textProps = {
    color: "#494C5B",
    size: 14 as TTextSize,
    nowrap: true,
    fitContent: true,
  };

  return (
    <Wrap inline={inline} ref={wrapRef}>
      <Root onClick={() => setOpened(!opened)} opened={opened} inline={inline} minWidth={minWidth}>
        {title && (
          <>
            <Text {...textProps} type="heading">{title}:</Text>
            &nbsp;
          </>
        )}
        <Text color="#494C5B" size={14} nowrap>{current.title}</Text>
        <ArrowDownIcon className="arrow" />
      </Root>
      <Tooltip opened={opened} inline={inline} minWidth={minWidth}>
        {
          options.map((option) => {
            return (
              <SelectRow
                key={option.value}
                {...textProps}
                onClick={() => {
                  if (!option.action) setSelected(option);
                  onSelect(option.value);
                  setOpened(false);
                }}
                selected={!option.action && current.value === option.value}
              >
                <span>{option.title}</span>
                {option.badge && <Badge>{option.badge}</Badge>}
                {option.info && (
                  <InfoTrigger onClick={(e) => e.stopPropagation()}>
                    <HoverTooltip
                      content={
                        <Text size={12} style={{ maxWidth: 240, display: "block" }}>
                          {option.info}
                        </Text>
                      }
                    >
                      <InfoIcon />
                    </HoverTooltip>
                  </InfoTrigger>
                )}
              </SelectRow>
            );
          })
        }
      </Tooltip>
    </Wrap>
  );
};

export default Dropdown;
